import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { Table, Tag, Tooltip } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { LinkOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { DataCrafterChildSession } from '../../types';
import './ChildSessionsPreview.less';

interface ChildSessionsPreviewProps {
    childSessions: DataCrafterChildSession[];
    openChildSession: (sessionId: string) => void;
    getUserById: (userId: string) => string;
}

const ChildSessionsPreview: React.FC<ChildSessionsPreviewProps> = ({
    childSessions,
    openChildSession,
    getUserById
}) => {
    // Define table columns
    const columns: ColumnsType<DataCrafterChildSession> = [
        {
            title: 'Session URL',
            dataIndex: 'sessionUrl',
            key: 'sessionUrl',
            render: (url: string, record: DataCrafterChildSession) => (
                <a className="session-link" onClick={() => openChildSession(record.sessionId)}>
                    <LinkOutlined /> {record.sessionId}
                </a>
            )
        },
        {
            title: 'Project',
            dataIndex: 'projectName',
            key: 'projectName'
        },
        {
            title: 'Application',
            dataIndex: 'applicationName',
            key: 'applicationName'
        },
        {
            title: 'State',
            dataIndex: 'state',
            key: 'state',
            render: (state: string, record: DataCrafterChildSession) => {
                const getStateLabel = (sessionState: string) => {
                    switch (sessionState.toLowerCase()) {
                        case 'waitingaction':
                            return 'Please review';
                        default:
                            return state;
                    }
                };

                // If there's an error, show tooltip with error message
                if (record.error) {
                    return (
                        <Tooltip title={record.error} placement="topLeft">
                            <Tag color="red" icon={<ExclamationCircleOutlined />}>
                                {getStateLabel(state)}
                            </Tag>
                        </Tooltip>
                    );
                }

                // Otherwise show state as tag with appropriate color
                const getStateColor = (sessionState: string) => {
                    switch (sessionState.toLowerCase()) {
                        case 'completed':
                        case 'success':
                        case 'waitingaction':
                            return 'green';
                        case 'notstarted':
                        case 'pending':
                            return 'blue';
                        case 'failed':
                        case 'error':
                            return 'red';
                        default:
                            return 'default';
                    }
                };

                return <Tag color={getStateColor(state)}>{getStateLabel(state)}</Tag>;
            }
        },
        {
            title: 'User',
            dataIndex: 'userId',
            key: 'userId',
            render: (userId: string) => getUserById(userId)
        },
        {
            title: 'Created',
            dataIndex: 'created',
            key: 'created',
            sorter: (a, b) => new Date(a.created).getTime() - new Date(b.created).getTime(),
            defaultSortOrder: 'descend',
            render: (created: string) => {
                // Format the timestamp to a readable date/time format
                const date = new Date(created);
                return (
                    <span>
                        {date.toLocaleDateString()} {date.toLocaleTimeString()}
                    </span>
                );
            }
        }
    ];

    return (
        <div className="child-sessions-preview">
            <Table
                columns={columns}
                dataSource={childSessions}
                rowKey="sessionId"
                pagination={false}
                size="small"
                className="child-sessions-table"
            />
        </div>
    );
};

export default observer(ChildSessionsPreview);
